// Én sandhed (spor "sandhed"): hintet, bykortet, Volt og teksterne ved årsskifte og tab spørger alle
// her, så de aldrig siger hinanden imod (anmelderne, runde 5: "Volts forslag og hint-baren peger hver
// sin vej").
//
// Reglen: for hver by, der mangler strøm, er der ét træk, og det er trænerens (cityAdvice), regnet
// igennem på en kopi af nettet. Hintet peger på det, bykortet viser det, og Volt gør det, hvis det er
// hans rolle, eller siger "det er dit træk", hvis det er spillerens. Volt bygger aldrig noget andet
// til den by. Flaskehalsen siges præcist: "for lidt produktion i nettet" eller "ledningen X-Y er fuld".

import { check, roleBlocked } from '../sim';
import type { ActionKind, GridNode, PlayerId, State } from '../sim';
import { isPowered } from '../sim/cities';
import { nodeById, pairKey } from '../sim/grid';
import type { Advice } from './coach';
import { adviceKind, adviseFor, cityAdvice, fullLines, netOf, opsTip } from './coach';

export type TruthRole = 'planner' | 'drift';

export type Shown = Advice & { display: 'hand' | 'ring'; partner?: boolean };

export interface HintContext {
  player: PlayerId;
  /** Spilles der med roller (makker eller Volt)? */
  roles: boolean;
}

export interface Move {
  kind: ActionKind;
  target?: string;
  opt?: string;
}

/** Handlingen bag et råd (art, mål, valg) uden tjek. null: rådet er ikke en handling. */
export function actionOf(a: Advice): Move | null {
  const kind = adviceKind(a);
  if (!kind) return null;
  switch (a.kind) {
    case 'hire':
      return { kind };
    case 'reinforce':
    case 'upgrade':
      return { kind, target: a.line };
    case 'shoo':
      return { kind, target: a.threat };
    case 'card':
      return { kind, target: a.threat, opt: a.opt };
    case 'power':
      return a.target ? { kind, target: a.target } : { kind };
    case 'build':
      return { kind, target: pairKey(a.from, a.to), opt: a.opt };
    case 'station':
    case 'battery':
    case 'flywheel':
      return { kind, target: a.node };
    default:
      return null;
  }
}

/** Et træk som nøgle: en ledning mellem a og b er den samme som mellem b og a. */
export function moveKey(kind: string, target: string | undefined): string {
  const t = target ?? '';
  return `${kind}:${kind === 'build_line' ? t.split('|').sort().join('|') : t}`;
}

/** Rådets nøgle (art og mål), eller art og by for råd, der ikke er et træk. */
export function adviceKey(a: Advice): string {
  const act = actionOf(a);
  if (act) return moveKey(act.kind, act.target);
  return `${a.kind}:${'node' in a ? a.node : ''}`;
}

/** Den by, rådet handler om (null: intet bestemt). */
export function cityOf(a: Advice): string | null {
  switch (a.kind) {
    case 'build':
      return a.why === 'producer' ? null : a.node;
    case 'upgrade':
      return a.node;
    case 'station':
      return a.for;
    case 'save':
    case 'calm':
      return a.node;
    case 'power':
      return a.city;
    default:
      return null;
  }
}

/** En aktiv by på kortet, der mangler strøm lige nu (og ikke allerede er sort). */
export function needy(n: GridNode): boolean {
  return !!n.city && n.active && !n.phantom && n.kind !== 'foreign' && n.city.darkTicks === 0 && !isPowered(n);
}

/** Hintet for spilleren. */
export function hintFor(state: State, ctx: HintContext): Shown | null {
  if (!ctx.roles) return adviseFor(state, undefined);
  if (ctx.player === 'p2') return driftHint(state);
  const raw = adviseFor(state, 'build');
  const kind = raw ? adviceKind(raw) : null;
  if (!raw || !kind || !roleBlocked(state, kind, 'p1')) return raw;
  // Spor: tillid (co-op runde 4: værten så "Thorsminde til Ringkøbing", gæsten "Thorsminde til
  // Thor Havvind"). Makkerens job vises, som makkeren selv får det: samme råd på begge skærme.
  const theirs = driftHint(state);
  const tk = theirs ? adviceKind(theirs) : null;
  return theirs && tk && roleBlocked(state, tk, 'p1') ? { ...theirs, partner: true } : { ...raw, partner: true };
}

/**
 * Driftsvagtens hint. Hans eget arbejde (storm, brud, gæster, kort) kommer først. Ellers står han på
 * Planlæggerens råd, så I ser det samme: en ny by, en opsparing eller "strømmen er på vej" er det samme
 * på begge skærme, og byggerådet er "Planlæggerens job". Mp-e2e målte før 14 af 181 prøver, hvor
 * gæsten skrev ét råd og værten et andet, fordi de to roller hver regnede deres egen rækkefølge.
 */
function driftHint(state: State): Shown | null {
  const mine = adviseFor(state, 'ops');
  const mk = mine ? adviceKind(mine) : null;
  // "Vent, montøren er på vej" har ingen handling, men er driftens besked (kun ops-rækkefølgen giver den).
  if (mine && (mine.kind === 'broken' || (mk && roleBlocked(state, mk, 'p1')))) return mine;
  const plan = adviseFor(state, 'build');
  if (!plan) return mine && !(mk && roleBlocked(state, mk, 'p2')) ? mine : null;
  const pk = adviceKind(plan);
  if (!pk || !roleBlocked(state, pk, 'p2')) return plan;
  const own = opsTip(state);
  if (own && !conflictsTruth(state, own)) return { ...own, display: plan.display };
  return { ...plan, partner: true };
}

/** Strider et råd mod sandheden for en by, det rører (et andet træk end det, byen skal have)? */
function conflictsTruth(state: State, a: Advice): boolean {
  const act = actionOf(a);
  if (!act) return false;
  return truthConflict(state, act, touches(state, act)) !== null;
}

/** Bykortets råd for én by, med `partner`, når trækket er makkerens (eller Volts) job. */
export function truthForCity(state: State, id: string, player: PlayerId = 'p1'): (Advice & { partner?: boolean }) | null {
  const a = cityTruths(state).get(id) ?? cityAdvice(state, id);
  if (!a) return null;
  const kind = adviceKind(a);
  if (kind && roleBlocked(state, kind, player)) return { ...a, partner: true };
  return a;
}

// ---------------------------------------------------------------------------
// Sandheden pr. by, regnet én gang pr. tick

interface Memo {
  key: string;
  map: Map<string, Advice | null>;
}
const memo = new WeakMap<State, Memo>();

function stamp(state: State): string {
  let l = '';
  for (const x of state.lines) l += `${x.id}${x.status[0]}${x.tier ?? ''},`;
  return `${state.tick}|${state.phase}|${state.budget}|${l}`;
}

/** Trænerens træk for hver by, der mangler strøm (og byer uden ledning). Samme svar hele ticket. */
export function cityTruths(state: State): Map<string, Advice | null> {
  const key = stamp(state);
  const m = memo.get(state);
  if (m && m.key === key) return m.map;
  const map = new Map<string, Advice | null>();
  if (state.phase === 'playing') {
    for (const n of state.nodes) {
      if (!n.city || !n.active || n.phantom || n.kind === 'foreign') continue;
      const lonely = !state.lines.some((x) => x.a === n.id || x.b === n.id);
      if (!needy(n) && !lonely) continue;
      map.set(n.id, cityAdvice(state, n.id));
    }
  }
  memo.set(state, { key, map });
  return map;
}

/** Byerne, et træk rører direkte (ledningens ender, stationens node). */
export function touches(state: State, m: Move): string[] {
  const t = m.target ?? '';
  switch (m.kind) {
    case 'build_line':
      return t.split('|').filter(Boolean);
    case 'upgrade_line':
    case 'bury_line': {
      const l = state.lines.find((x) => x.id === t);
      return l ? [l.a, l.b] : [];
    }
    case 'upgrade_station':
    case 'build_battery':
    case 'build_flywheel':
      return t ? [t] : [];
    default:
      return [];
  }
}

/** Træk, der bygger eller udvider nettet til en by: de må ikke stride mod byens træk. */
export const FIX_KINDS: ReadonlySet<ActionKind> = new Set<ActionKind>(['build_line', 'upgrade_line', 'upgrade_station', 'build_battery']);

/**
 * Strider trækket mod sandheden for en by, det rører? Svaret er byens id, når byen mangler strøm, og
 * kortet siger et andet træk for den; ellers null.
 */
export function truthConflict(state: State, m: Move, nodes: readonly string[]): string | null {
  if (!FIX_KINDS.has(m.kind)) return null;
  const truths = cityTruths(state);
  const k = moveKey(m.kind, m.target);
  for (const id of nodes) {
    const n = nodeById(state, id);
    if (!n || !needy(n)) continue;
    const a = truths.get(id);
    if (!a || !actionOf(a)) continue;
    if (adviceKey(a) !== k) return id;
  }
  return null;
}

export interface CityMove {
  city: GridNode;
  advice: Advice;
  move: Move;
  /** Hvem trækket tilhører, når der spilles med roller (null: alle må). */
  owner: PlayerId | null;
  /** Kan trækket gøres nu af ejeren (eller af alle uden roller)? */
  ok: boolean;
  cost: number;
}

/** De byer i nød, hvor sandheden er et træk, med hvem der skal gøre det. Mest presset by først. */
export function cityMoves(state: State): CityMove[] {
  const out: CityMove[] = [];
  for (const [id, a] of cityTruths(state)) {
    if (!a) continue;
    const city = nodeById(state, id);
    const move = actionOf(a);
    if (!city?.city || !move) continue;
    const owner: PlayerId | null = state.roles ? (roleBlocked(state, move.kind, 'p1') ? 'p2' : 'p1') : null;
    const c = check(state, move.kind, move.target, move.opt, owner ?? 'p1');
    out.push({ city, advice: a, move, owner, ok: c.ok, cost: c.ok ? c.cost : 0 });
  }
  return out.sort((a, b) => (b.city.city?.clock ?? 0) - (a.city.city?.clock ?? 0) || (b.city.city?.demandMw ?? 0) - (a.city.city?.demandMw ?? 0));
}

// ---------------------------------------------------------------------------
// Flaskehalsen

export type Bottleneck =
  | { kind: 'noline' }
  | { kind: 'broken'; a: string; b: string }
  | { kind: 'full'; a: string; b: string; line: string }
  | { kind: 'production' }
  | { kind: 'ok' };

/** Hvorfor mangler byen strøm? Præcist: én knækket eller fuld ledning, eller for lidt produktion i nettet. */
export function bottleneck(state: State, id: string): Bottleneck {
  const n = nodeById(state, id);
  if (!n?.city) return { kind: 'ok' };
  const own = state.lines.filter((l) => l.a === id || l.b === id);
  if (own.length === 0) return { kind: 'noline' };
  if (isPowered(n) && !n.city.out) return { kind: 'ok' };
  const name = (x: string): string => nodeById(state, x)?.name ?? x;
  const broken = own.find((l) => l.status === 'broken' || l.halfDown);
  if (broken && own.every((l) => l.status !== 'ok')) return { kind: 'broken', a: name(broken.a), b: name(broken.b) };
  const full = fullLines(state, id)[0];
  // Har nettet strøm til overs, er det ledningen; ellers er det produktionen.
  const net = netOf(state, id);
  let spare = 0;
  for (const m of state.nodes) if (m.producer && m.active && net.has(m.id)) spare += Math.max(0, m.producer.availMw - m.producer.usedMw);
  const lack = Math.max(0, n.city.demandMw - n.city.servedMw);
  if (full && spare >= Math.min(lack, 10)) return { kind: 'full', a: name(full.line.a), b: name(full.line.b), line: full.line.id };
  if (broken) return { kind: 'broken', a: name(broken.a), b: name(broken.b) };
  if (full && spare < 10) return { kind: 'production' };
  if (full) return { kind: 'full', a: name(full.line.a), b: name(full.line.b), line: full.line.id };
  return { kind: 'production' };
}

/** Flaskehalsen som kort dansk tekst uden bynavnet: "ledningen Skive-Viborg er fuld". */
export function bottleneckText(b: Bottleneck): string {
  switch (b.kind) {
    case 'noline':
      return 'ingen ledning endnu';
    case 'broken':
      return `ledningen ${b.a}-${b.b} er knækket`;
    case 'full':
      return `ledningen ${b.a}-${b.b} er fuld`;
    case 'production':
      return 'for lidt produktion i nettet';
    case 'ok':
      return 'får nok';
  }
}

/** Kan en ledning bygges fra byens net til noden (pengene og fasen ikke medregnet)? */
export function reachable(state: State, net: ReadonlySet<string>, to: string): boolean {
  const free: State = { ...state, phase: 'playing', budget: 1e9 };
  for (const id of net) {
    const n = nodeById(state, id);
    if (!n || !n.active || n.phantom || n.kind === 'foreign') continue;
    for (const medium of ['air', 'cable', 'sea'] as const) if (check(free, 'build_line', pairKey(id, to), medium, 'p1').ok) return true;
  }
  return false;
}

/**
 * En producent uden for byens net med strøm til overs, som en ledning fra nettet faktisk kan nå
 * (ingen ledning til Holland, ingen ledning over land, der kun kan bygges som søkabel uden kabler).
 * Til teksterne ved årsskifte og tab, så de aldrig foreslår et træk, der ikke kan gøres.
 */
export function spareProducer(state: State, node: string, min = 20): GridNode | null {
  const net = netOf(state, node);
  const spare = (m: GridNode): number => (m.producer ? m.producer.availMw - m.producer.usedMw : 0);
  const list = state.nodes
    .filter((m) => m.producer && m.active && !m.phantom && m.kind !== 'foreign' && !net.has(m.id) && spare(m) >= min)
    .sort((p, q) => spare(q) - spare(p));
  return list.find((m) => reachable(state, net, m.id)) ?? null;
}

/** "Viborg: ledningen Skive-Viborg er fuld", eller null når byen får nok. */
export function bottleneckLine(state: State, id: string): string | null {
  const b = bottleneck(state, id);
  if (b.kind === 'ok') return null;
  return `${nodeById(state, id)?.name ?? id}: ${bottleneckText(b)}`;
}
